import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { env } from '@/lib/env';

/**
 * Sign-in / create-account actions for guest surfaces (guest header, the
 * reading-level selector, and the account gate). Both kick off Auth0
 * Universal Login with `returnTo` set to the current path so the visitor
 * lands back where they were after the callback (a brand-new account is
 * then routed to onboarding by `ProtectedRoute`).
 */
export function useGuestAuthActions() {
  const { loginWithRedirect } = useAuth0();
  const location = useLocation();
  const returnTo = location.pathname + location.search;

  const go = useCallback(
    (extra: Record<string, string> = {}) =>
      loginWithRedirect({
        appState: { returnTo },
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: env.auth0Audience,
          ...extra,
        },
      }),
    [loginWithRedirect, returnTo],
  );

  const signIn = useCallback(() => go(), [go]);
  const createAccount = useCallback(() => go({ screen_hint: 'signup' }), [go]);

  return { signIn, createAccount };
}
